import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import { getHostDomain } from 'frontend-utils/utils/domain-checks';
import HotelAndMarketNavigationNavigationLink from 'lumen/components/hotel-and-market/navigation/navigation-link';
import SettingsDrawerNavigationHeader from 'lumen/components/settings/drawer/navigation-header';
import ENV from 'lumen/config/environment';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';

const FEATURE_FLAGS = {
  autopilot: ['pricing_assistant.pms.push_integrated'],
  benchmarkCompetitors: [
    'benchmark_insight.settings.compset.view',
    'benchmark_insight.settings.smart_compset.view',
    'benchmark_insight.settings.portfolio_compset.view',
  ],
  budgetAndForecast: ['business_intelligence.settings.budget_and_forecast'],
  competitors: [
    'settings.compset.app.view',
    'settings.compset.rms.view',
    'benchmark_insight.settings.compset.view',
    'benchmark_insight.settings.smart_compset.view',
    'benchmark_insight.settings.portfolio_compset.view',
  ],
  hotelAccess: ['settings.hotel_access.edit'],
  otb: ['otb.selfservice.view'],
  pmsIntegration: [
    'pricing_assistant.pms.push_integrated',
    'pricing_assistant.signup.open',
    'pricing_assistant.signup.in_progress',
  ],
  pos: ['app.rate_strategy'],
  pricingAssistant: ['app.pricing_assistant'],
  rates: ['app.rates'],
  reports: ['reports.view.settings'],
  spider: ['spider.settings.edit'],
} as const;

interface SettingsDrawerSidebarSignature {
  Args: {};
}

export default class SettingsDrawerSidebar extends Component<SettingsDrawerSidebarSignature> {
  @service declare access: Services['access'];
  @service declare centralMapping: Services['central-mapping'];
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarket: HotelAndMarketServiceWithHotel;
  @service declare navigation: Services['navigation'];
  @service declare router: Services['router'];
  @service declare theme: Services['theme'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isSettingsDrawerOpen = true;

  get channelManagerSettingsUrl(): string {
    const { id } = this.hotelAndMarket.hotel;
    return `${getHostDomain({ frontendGateway: ENV.frontendGateway })}/apigateway/v1/app/channel-management/channelmanager/outgoing?subscription_id=${id}&page=settings-rateplans`;
  }

  get hasBenchmarkCompetitorsSettingsAccess(): boolean {
    return FEATURE_FLAGS.benchmarkCompetitors.some((feature) =>
      this.hasFeatureForSettings(feature),
    );
  }

  get hasBudgetAndForecast(): boolean {
    return FEATURE_FLAGS.budgetAndForecast.some((feature) =>
      this.hasFeatureForSettings(feature),
    );
  }

  get hasBusinessIntelligenceGroup(): boolean {
    return (
      this.hasFeatureForSettings('spider.settings.edit') ||
      this.hasBudgetAndForecast ||
      this.hasCurrencyConversion
    );
  }

  get hasCentralMapping(): boolean {
    return this.centralMapping.hasCentralMappingHotelAccess;
  }

  get hasCompetitorsSettings(): boolean {
    return (
      this.hasFeatureForSettings('settings.compset.app.view') ||
      this.hasFeatureForSettings('settings.compset.rms.view') ||
      this.hasBenchmarkCompetitorsSettingsAccess
    );
  }

  get hasCurrencyConversion(): boolean {
    return this.currencyConversion.hasCurrencyConversionAccess;
  }

  get hasGeneralDefaults(): boolean {
    return (
      this.access.hasUnifiedSettingsChannelDistributionView ||
      this.access.hasUnifiedSettingsChannelDistributionEdit
    );
  }

  get hasGeneralGroup(): boolean {
    const hasMapping =
      this.hasCentralMapping ||
      this.hasFeatureForSettings('benchmark_insight.pms.view');

    return (
      this.hasUserAccessSettings ||
      this.hasFeatureForSettings('settings.hotel_access.edit') ||
      this.hasFeatureForSettings('reports.view.settings') ||
      this.hasCompetitorsSettings ||
      hasMapping ||
      this.hasFeatureForSettings('app.rate_strategy')
    );
  }

  get hasHotelAccessSettings(): boolean {
    return FEATURE_FLAGS.hotelAccess.some((feature) =>
      this.hasFeatureForSettings(feature),
    );
  }

  get hasHotelDetails(): boolean {
    return (
      this.access.hasUnifiedSettingsSubscriptionProfileView ||
      this.access.hasUnifiedSettingsSubscriptionProfileEdit
    );
  }

  get hasIntegrationsGroup(): boolean {
    return this.hasOtbSelfService || this.hasPmsIntegrationTab;
  }

  get hasMyPropertyGroup(): boolean {
    return this.hasHotelDetails || this.hasGeneralDefaults;
  }

  get hasOtbSelfService(): boolean {
    return FEATURE_FLAGS.otb.some((feature) =>
      this.hasFeatureForSettings(feature),
    );
  }

  get hasPersonalSettingsGroup(): boolean {
    return (
      this.access.hasPersonalSettingsTab ||
      this.access.canEditPersonalSettingsPassword
    );
  }

  get hasPmsIntegrationTab(): boolean {
    return FEATURE_FLAGS.pmsIntegration.some((feature) =>
      this.hasFeatureForSettings(feature),
    );
  }

  get hasPricingAssistantSettings(): boolean {
    if (this.navigation.hasUnifiedHotelView) {
      return (
        !this.navigation.hasFeatureInSomeSubscription(
          'smart_insights.recommendations_hide_prima',
        ) &&
        this.navigation.hasFeatureInSomeSubscription('app.pricing_assistant')
      );
    }

    return this.access.hasPricingAssistantTab;
  }

  get hasPricingAssistantSettingsAutopilotTab(): boolean {
    if (this.navigation.hasUnifiedHotelView) {
      return (
        this.navigation.hasFeatureInSomeSubscription(
          'pricing_assistant.pms.push_integrated',
        ) &&
        this.navigation.hasFeatureInSomeSubscription(
          'pricing_assistant.pms.autopilot',
        )
      );
    }

    return this.access.hasPricingAssistantSettingsAutopilotTab;
  }

  get hasPricingAssistantSettingsPreferredRateTab(): boolean {
    if (this.navigation.hasUnifiedHotelView) {
      return (
        this.navigation.hasFeatureInSomeSubscription('app.pricing_assistant') &&
        !this.navigation.hasFeatureInSomeSubscription(
          'pricing_assistant.pms.push_integrated',
        )
      );
    }

    return this.access.hasPricingAssistantSettingsPreferredRateTab;
  }

  get hasPricingGroup(): boolean {
    return (
      this.hasPricingAssistantSettingsPreferredRateTab ||
      this.hasFeatureForSettings('app.pricing_assistant') ||
      this.hasPricingAssistantSettingsAutopilotTab ||
      this.hasRates
    );
  }

  get hasRates(): boolean {
    return FEATURE_FLAGS.rates.some((feature) =>
      this.hasFeatureForSettings(feature),
    );
  }

  get hasRateStrategyAccess(): boolean {
    return FEATURE_FLAGS.pos.some((feature) =>
      this.hasFeatureForSettings(feature),
    );
  }

  get hasReportSettings(): boolean {
    return FEATURE_FLAGS.reports.some((feature) =>
      this.hasFeatureForSettings(feature),
    );
  }

  get hasSpiderEdit(): boolean {
    return FEATURE_FLAGS.spider.some((feature) =>
      this.hasFeatureForSettings(feature),
    );
  }

  get hasUserAccessSettings(): boolean {
    return this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.USER_ACCESS);
  }

  get isCentralMappingTabActive(): boolean {
    const { currentRouteName } = this.router;

    return Boolean(
      currentRouteName?.includes('hotel.settings.central-mapping') ||
      currentRouteName?.includes('hotel.settings.mapping.benchmark'),
    );
  }

  get isMyOTBActive(): boolean {
    return Boolean(
      this.router.currentRouteName?.includes('hotel.settings.myotb'),
    );
  }

  hasFeatureForSettings(featureName: string): boolean {
    if (this.navigation.hasUnifiedHotelView) {
      return this.navigation.hasFeatureInSomeSubscription(featureName);
    }

    return this.access.hasFeature(featureName);
  }

  @action
  onClickCentralMappingTab(): void {
    if (this.hasCentralMapping && !this.isCentralMappingTabActive) {
      this.usageAnalytics.track('Mapping Menu Page Opened', {
        eventTrigger: 'pms-mapping-settings',
        product: 'central-mapping',
      });
    }
  }

  @action
  toggleNavbar(): void {
    this.isSettingsDrawerOpen = !this.isSettingsDrawerOpen;
  }

  @action
  trackBudgetSettingsClick(): void {
    this.usageAnalytics.track('Budget Page Viewed', {
      eventTrigger: 'settings-budget-clicked',
    });
  }

  @action
  trackCurrencyConversionClick(): void {
    this.usageAnalytics.track('Currency Settings Page Opened', {
      eventTrigger: 'currency-settings',
      product: 'custom-currency',
    });
  }

  @action
  trackForecastSettingsClick(): void {
    this.usageAnalytics.track('Forecast Page Viewed', {
      eventTrigger: 'settings-forecast-clicked',
    });
  }

  <template>
    <div
      class="settings-drawer
        {{if
          this.isSettingsDrawerOpen
          'settings-drawer--open'
          'settings-drawer--close'
        }}
        "
    >
      <SettingsDrawerNavigationHeader
        @isDrawerOpen={{this.isSettingsDrawerOpen}}
        @toggleNavbar={{this.toggleNavbar}}
      />
      {{#if this.isSettingsDrawerOpen}}
        <ul class="text-300--medium settings-drawer__content flex flex-col">
          {{#if this.hasPersonalSettingsGroup}}
            <li class="settings-drawer__group">
              <h4
                class="settings-drawer__group-title"
                data-test-id="settings-group-personal"
              >
                {{t "settings.section.personal_settings"}}
              </h4>
              <ul class="settings-drawer__list">
                {{#if this.access.hasPersonalSettingsTab}}
                  <li>
                    <LinkTo
                      @route="hotel.settings.personal"
                      class="settings-drawer__link"
                      data-test-id="settings-item-account"
                    >
                      {{t "settings.personal.title"}}
                    </LinkTo>
                  </li>
                {{/if}}
                {{#if this.access.canEditPersonalSettingsPassword}}
                  <li>
                    <LinkTo
                      @route="hotel.settings.security"
                      class="settings-drawer__link"
                      data-test-id="settings-item-security"
                    >
                      {{t "settings.security.title"}}
                    </LinkTo>
                  </li>
                {{/if}}

                {{#if this.theme.hasPermissions}}
                  <li>
                    <LinkTo
                      @route="hotel.settings.theme"
                      class="settings-drawer__link"
                      data-test-id="settings-item-theme"
                    >
                      {{t "settings.theme.title"}}
                    </LinkTo>
                  </li>
                {{/if}}
              </ul>
            </li>
          {{/if}}

          {{#if this.hasMyPropertyGroup}}
            <li class="settings-drawer__group">
              <h4
                class="settings-drawer__group-title"
                data-test-id="settings-group-my-property"
              >
                {{t "settings.section.my_hotel"}}
              </h4>
              <ul class="settings-drawer__list">
                {{#if this.hasHotelDetails}}
                  <li>
                    <LinkTo
                      @route="hotel.settings.my-property.hotel-details"
                      class="settings-drawer__link"
                      data-test-id="settings-item-hotel-details"
                    >
                      {{t "settings.my_hotel.hotel_details.nav_label"}}
                    </LinkTo>
                  </li>
                {{/if}}
                {{#if this.hasGeneralDefaults}}
                  <li>
                    <LinkTo
                      @route="hotel.settings.my-property.general-defaults"
                      class="settings-drawer__link"
                      data-test-id="settings-item-general-defaults"
                    >
                      {{t "settings.my_hotel.general_defaults.nav_label"}}
                    </LinkTo>
                  </li>
                {{/if}}
                {{#if this.access.hasChannelManagerTab}}
                  <li>
                    <a
                      class="settings-drawer__link"
                      data-test-id="settings-item-channel-manager-settings"
                      href={{this.channelManagerSettingsUrl}}
                    >
                      {{t
                        "settings.my_hotel.channel_manager_settings.nav_label"
                      }}
                    </a>
                  </li>
                {{/if}}
              </ul>
            </li>
          {{/if}}

          {{#if this.hasGeneralGroup}}
            <li class="settings-drawer__group">
              <h4
                class="settings-drawer__group-title"
                data-test-id="settings-group-general"
              >
                {{t "settings.general"}}
              </h4>
              <ul class="settings-drawer__list">
                {{#if this.hasUserAccessSettings}}
                  <li>
                    <LinkTo
                      @route="hotel.settings.users"
                      class="settings-drawer__link"
                      data-test-id="settings-item-users"
                    >
                      {{t "settings.section.users"}}
                    </LinkTo>
                  </li>
                {{/if}}
                {{#if this.hasHotelAccessSettings}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.hotels"
                      class="settings-drawer__link"
                      id="ic-product-tour-settings-hotelaccess-tab"
                    >
                      {{t "settings.section.hotel_access"}}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
                {{#if this.hasReportSettings}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @queryParams={{hash
                        ownReports=(not this.access.isStaffUser)
                      }}
                      @routeName="hotel.settings.reports"
                      class="settings-drawer__link"
                      id="ic-product-tour-settings-reports-tab"
                    >
                      {{t "settings.section.scheduled_reports"}}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
                <li>
                  <LinkTo
                    @route="hotel.settings.activity"
                    class="settings-drawer__link"
                    data-test-id="settings-item-activity"
                  >
                    {{t "settings.section.activity_history"}}
                  </LinkTo>
                </li>
                {{#if this.hasCompetitorsSettings}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.competitors"
                      class="settings-drawer__link"
                      data-test-id="settings-item-competitors"
                    >
                      {{t "general.competitors"}}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
                {{#if this.hasCentralMapping}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @currentWhen={{this.isCentralMappingTabActive}}
                      @routeName="hotel.settings.central-mapping.menu"
                      class="settings-drawer__link flex flex-row items-center justify-between"
                      {{on "click" this.onClickCentralMappingTab}}
                    >
                      {{t "settings.central-mapping.mapping"}}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
                {{#if this.hasRateStrategyAccess}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.pos"
                      class="settings-drawer__link"
                      id="ic-product-tour-settings-pos-tab"
                    >
                      {{t "general.pos"}}
                      ({{t "general.pos_short"}})
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
              </ul>
            </li>
          {{/if}}

          {{#if this.hasIntegrationsGroup}}
            <li class="settings-drawer__group">
              <h4
                class="settings-drawer__group-title"
                data-test-id="settings-group-integrations"
              >
                {{t "settings.section.integrations"}}
              </h4>
              <ul class="settings-drawer__list">
                {{#if this.hasOtbSelfService}}
                  <li>
                    {{! Disable the LinkTo transition when you're already on My OTB and click on the tab. If this is not done
                      you could otherwise end up on the empty 'parent' myotb route which does not serve anything as
                      the redirect logic is in the aftermodal hook. The (after)model hook only gets executed when
                      you're transitioning from a different route and not from a sub route. }}
                    <HotelAndMarketNavigationNavigationLink
                      @disabled={{this.isMyOTBActive}}
                      @routeName="hotel.settings.myotb"
                      class="settings-drawer__link"
                    >
                      {{t "rates.on_the_books"}}
                      ({{t "rates.OTB"}})
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
                {{#if this.hasPmsIntegrationTab}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.pms-integration"
                      class="settings-drawer__link"
                      data-test-id="settings-item-pms-integration"
                    >
                      {{t
                        "pricing_assistant.settings.navigation.label.pms_integration"
                      }}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
              </ul>
            </li>
          {{/if}}

          {{#if this.hasBusinessIntelligenceGroup}}
            <li class="settings-drawer__group">
              <h4
                class="settings-drawer__group-title"
                data-test-id="settings-group-business-intelligence"
              >
                {{t "settings.section.business_intelligence"}}
              </h4>
              <ul class="settings-drawer__list">
                {{#if this.hasSpiderEdit}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.spider"
                      class="settings-drawer__link"
                      data-test-id="spider-settings"
                    >
                      {{t "settings.section.business_intelligence"}}
                      ({{t "settings.section.bi"}})
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
                {{#if this.hasBudgetAndForecast}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.budget"
                      class="settings-drawer__link ic-product-tour-49285022-settings-budget-tab"
                      data-test-id="budget-tab"
                      {{on "click" this.trackBudgetSettingsClick}}
                    >
                      {{t "settings.budget.title"}}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.forecast"
                      class="settings-drawer__link"
                      data-test-id="forecast-tab"
                      {{on "click" this.trackForecastSettingsClick}}
                    >
                      {{t "settings.forecast.title"}}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
                {{#if this.hasCurrencyConversion}}
                  <li>
                    <LinkTo
                      @route="hotel.settings.currency-conversion"
                      class="settings-drawer__link"
                      data-test-id="currency-conversion-tab"
                      {{on "click" this.trackCurrencyConversionClick}}
                    >
                      {{t "general.currency"}}
                    </LinkTo>
                  </li>
                {{/if}}
              </ul>
            </li>
          {{/if}}

          {{#if this.hasPricingGroup}}
            <li class="settings-drawer__group">
              <h4
                class="settings-drawer__group-title"
                data-test-id="settings-group-pricing"
              >
                {{t "settings.section.pricing"}}
              </h4>
              <ul class="settings-drawer__list">
                {{#if this.hasRates}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.taxes"
                      class="settings-drawer__link"
                    >
                      {{t "general.taxes"}}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
                {{#if this.hasPricingAssistantSettingsPreferredRateTab}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.preferred-rate"
                      class="settings-drawer__link"
                      data-test-id="settings-item-preferred-rate"
                    >
                      {{t
                        "pricing_assistant.settings.navigation.label.preferred_rate"
                      }}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
                {{#if this.hasPricingAssistantSettings}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.recommendations"
                      class="settings-drawer__link"
                    >
                      {{t
                        "pricing_assistant.settings.navigation.label.recommendation"
                      }}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
                {{#if this.hasPricingAssistantSettingsAutopilotTab}}
                  <li>
                    <HotelAndMarketNavigationNavigationLink
                      @routeName="hotel.settings.autopilot"
                      class="settings-drawer__link"
                      data-test-id="settings-item-autopilot"
                    >
                      {{t
                        "pricing_assistant.autopilot.settings.navigation.label"
                      }}
                    </HotelAndMarketNavigationNavigationLink>
                  </li>
                {{/if}}
              </ul>
            </li>
          {{/if}}
        </ul>
      {{/if}}
    </div>
  </template>
}
